/** A temporary route request assembled in the chat; only the names/IDs are sent. */
import { isAgentFamily } from '../store/graphFamily';
import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { DEV_TOOLS } from '../devtools/diagnostics';
import { dispatchStarter } from '../a2ui/actions';
import { useSession } from '../store/sessionStore';
import { MUSEUM_POINTS, type MuseumPoint } from './objects';
import { MAX_NAVIGATION_VIA } from './navigation';

type Choice = {
  selected: MuseumPoint | null;
  via: MuseumPoint[];
  choose: (point: MuseumPoint | null) => void;
  addVia: (point: MuseumPoint) => void;
  removeVia: (id: string) => void;
  reset: () => void;
};

export const usePointChoice = create<Choice>((set) => ({
  selected: null,
  via: [],
  choose: (point) => set({ selected: point }),
  addVia: (point) => set((state) => ({
    selected: null,
    via: state.via.some((item) => item.id === point.id) || state.via.length >= MAX_NAVIGATION_VIA
      ? state.via : [...state.via, point],
  })),
  removeVia: (id) => set((state) => ({ via: state.via.filter((point) => point.id !== id) })),
  reset: () => set({ selected: null, via: [] }),
}));

export function searchPoints(query: string): MuseumPoint[] {
  const key = query.trim().toLocaleLowerCase('ru').replaceAll('ё', 'е');
  return key ? MUSEUM_POINTS.filter((point) => point.label.toLocaleLowerCase('ru').replaceAll('ё', 'е').includes(key)) : MUSEUM_POINTS;
}

export function routeRequest(destination: MuseumPoint, via: MuseumPoint[]): string {
  const named = (point: MuseumPoint) => `«${point.label}» (id ${point.id})`;
  return via.length
    ? `Проведи пешком через ${via.map(named).join('; ')} к ${named(destination)}. Промежуточные точки посети в указанном порядке.`
    // Not «пешком»: a visitor far from the park gets the transport asked (live dev 12.10.2026, thread ebe6923d).
    : `Проведи к ${named(destination)}.`;
}

/** The route button's key (`graphs/peterhof_plan/starter.py`, `route:`): the museum points, the destination
 *  last. The graph makes the turn's route call in code — the line alone went to «points on the map» in
 *  1–2 of 4 fresh threads (live dev 12.10.2026). */
export function routeStarter(destination: MuseumPoint, via: MuseumPoint[]): string {
  return `route:${[...via, destination].map((point) => point.id).join(';')}`;
}

/** Sends the route request with its button key — «Идти сюда», «Как добраться». */
export function askRoute(destination: MuseumPoint, via: MuseumPoint[]): void {
  dispatchStarter(routeRequest(destination, via), routeStarter(destination, via));
}

// The staged stops belong to the conversation, not to whoever draws them: the
// search lives in a drawer tab that mounts and unmounts as the visitor opens it,
// so the reset follows the session's own thread/graph change instead.
useSession.subscribe((state, previous) => {
  if (state.threadId !== previous.threadId || state.assistantId !== previous.assistantId) {
    usePointChoice.getState().reset();
  }
});

export function PointActions() {
  const selected = usePointChoice((state) => state.selected);
  const via = usePointChoice((state) => state.via);
  if (!selected) return null;
  const alreadyVia = via.some((point) => point.id === selected.id);
  return <div className="point-choice__actions" aria-label={`Выбрана точка ${selected.label}`}>
    <strong>{selected.label}</strong>
    <button type="button" className="btn btn--primary" disabled={alreadyVia} onClick={() => {
      askRoute(selected, via);
      usePointChoice.getState().reset();
    }}>Идти сюда</button>
    <button type="button" className="btn" disabled={alreadyVia || via.length >= MAX_NAVIGATION_VIA} onClick={() => usePointChoice.getState().addVia(selected)}>Через эту точку</button>
    {via.length >= MAX_NAVIGATION_VIA ? <span>Можно выбрать до трёх промежуточных точек.</span> : null}
    <button type="button" className="btn btn--quiet" onClick={() => usePointChoice.getState().choose(null)}>Отмена</button>
  </div>;
}

export default function PointChooser() {
  const assistantId = useSession((state) => state.assistantId);
  const threadId = useSession((state) => state.threadId);
  const via = usePointChoice((state) => state.via);
  const selected = usePointChoice((state) => state.selected);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  useEffect(() => { setOpen(false); setQuery(''); }, [threadId, assistantId]);
  if (!DEV_TOOLS || !isAgentFamily(assistantId)) return null;
  const matches = searchPoints(query);
  return <div className="point-choice">
    <button type="button" className="btn btn--quiet" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
      Найти точку маршрута{via.length ? ` · через ${via.length}` : ''}
    </button>
    {via.length ? <div className="point-choice__via" aria-label="Промежуточные точки">
      {via.map((point, index) => <button className="btn btn--quiet" type="button" key={point.id} onClick={() => usePointChoice.getState().removeVia(point.id)} title="Убрать промежуточную точку">
        {index + 1}. {point.label} ×
      </button>)}
    </div> : null}
    {open ? <div className="point-choice__search">
      <label htmlFor="point-search">Найти объект музея</label>
      <input id="point-search" type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Дворец, фонтан, вход…" />
      <p>{matches.length} из {MUSEUM_POINTS.length} точек</p>
      <div className="point-choice__results" role="listbox" aria-label="Точки музея">
        {matches.map((point) => <button type="button" role="option" aria-selected={selected?.id === point.id} key={point.id} onClick={() => usePointChoice.getState().choose(point)}>
          {point.label}{MUSEUM_POINTS.filter((item) => item.label === point.label).length > 1 ? ` · №${point.id}` : ''}
        </button>)}
      </div>
      <PointActions />
    </div> : null}
  </div>;
}
